import { useCallback, useEffect, useRef } from "react";

/**
 * 短時間に連続して呼ばれた値をまとめて 1 回だけ `flush` に渡すフック。
 *
 * 用途: 一括ダウンロード完了時の「ダウンロード既読」登録など、同じ API を
 * 件数ぶん同時に叩くと BE / Redis 側で接続が枯渇する処理を、配列 1 回の
 * リクエストに畳む。`add` を呼んでから `delay` ms 以内に届いた値は同じ
 * バッチに入る。重複した値は 1 つにまとめる。
 *
 * - `flush` は常に最新の関数が呼ばれる（依存配列の管理は不要）。
 * - アンマウント時に未送信分があれば、その場で `flush` して取りこぼさない。
 *
 * @param flush まとめた値の配列を受け取る関数
 * @param delay まとめ待ちの時間 (ms)。既定値 50ms。
 * @returns 値を 1 件追加する関数
 */
export const useBatchedCall = <T,>(
  flush: (items: T[]) => void | Promise<unknown>,
  delay = 50,
): ((item: T) => void) => {
  const pendingRef = useRef<T[]>([]);
  const timerRef = useRef<number | null>(null);
  const flushRef = useRef(flush);
  flushRef.current = flush;

  const runFlush = useCallback(() => {
    timerRef.current = null;
    const items = Array.from(new Set(pendingRef.current));
    pendingRef.current = [];
    if (items.length === 0) return;
    void flushRef.current(items);
  }, []);

  const add = useCallback(
    (item: T) => {
      pendingRef.current.push(item);
      if (timerRef.current === null) {
        timerRef.current = window.setTimeout(runFlush, delay);
      }
    },
    [delay, runFlush],
  );

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        runFlush();
      }
    },
    [runFlush],
  );

  return add;
};

export default useBatchedCall;
